import { useEffect, useRef, useState } from 'react';
import {
  tallyOptions,
  voterCount,
  nonVoters,
  type DraftPoll,
  type DraftOption,
  type OptionTally,
  type PollMember,
} from '@ff/core';
import {
  fetchLeagueMembers,
  fetchDraftPoll,
  createDraftPoll,
  resetDraftPoll,
  addDraftOption,
  removeDraftOption,
  voteDraftPoll,
  lockDraftPoll,
  reopenDraftPoll,
  ApiError,
} from '../lib/api.ts';
import { useMe } from '../lib/me.tsx';

/** Refresh cadence so other people's votes show up without a reload. */
const POLL_MS = 15_000;

/**
 * The draft scheduler: an admin-run, approval vote that settles when the league
 * drafts. Phase one narrows the date, phase two narrows the time; the
 * commissioner locks each. Lives at the top of the War Room.
 */
export function DraftScheduler({
  season,
  readOnly = false,
}: {
  season: number;
  readOnly?: boolean;
}) {
  const me = useMe();
  const isAdmin = me?.isCommissioner === true && !readOnly;
  const [poll, setPoll] = useState<DraftPoll | null | undefined>(undefined); // undefined = loading
  const [roster, setRoster] = useState<PollMember[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Same monotonic-sequence guard as the trash board: a background poll that
  // started before a vote/lock must not overwrite the fresher result.
  const seqRef = useRef(0);
  const appliedRef = useRef(0);
  const apply = (next: DraftPoll | null, seq: number) => {
    if (seq < appliedRef.current) return;
    appliedRef.current = seq;
    setPoll(next);
    setError(null);
  };

  // The roster barely changes, so it loads once and feeds the "still waiting on"
  // list. A failure just means we fall back to showing who HAS voted.
  useEffect(() => {
    let alive = true;
    fetchLeagueMembers()
      .then((m) => alive && setRoster(m))
      .catch(() => {
        /* no roster, no nag list */
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setPoll(undefined);
    seqRef.current = 0;
    appliedRef.current = 0;
    const load = () => {
      const seq = ++seqRef.current;
      fetchDraftPoll(season)
        .then((p) => alive && apply(p, seq))
        .catch(() => {
          /* a dropped poll recovers on the next tick */
        });
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [season]);

  const run = (fn: () => Promise<DraftPoll | null>) => {
    const seq = ++seqRef.current;
    setError(null);
    return fn()
      .then((p) => apply(p, seq))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Something went wrong'));
  };

  if (poll === undefined) {
    return <p className="font-mono text-xs text-chalk-dim">loading the draft vote…</p>;
  }

  // No poll yet: only the commissioner sees a way to start one.
  if (poll === null) {
    if (!isAdmin) return null;
    return (
      <Section>
        <Heading>Draft Schedule</Heading>
        <p className="font-editorial text-sm text-chalk-dim">
          Nothing scheduled yet. Start a vote and add the dates you're considering.
        </p>
        <button onClick={() => run(() => createDraftPoll(season))} className={btnPrimary}>
          Start the draft vote
        </button>
        {error && <ErrorLine>{error}</ErrorLine>}
      </Section>
    );
  }

  if (poll.phase === 'final' && poll.final) {
    return (
      <Section>
        <FinalCard date={poll.final.date} time={poll.final.time} />
        {isAdmin && (
          <div className="flex gap-2">
            <button onClick={() => run(() => reopenDraftPoll(season))} className={btnGhost}>
              Reopen time vote
            </button>
            <button onClick={() => run(() => resetDraftPoll(season))} className={btnGhostDanger}>
              Start over
            </button>
          </div>
        )}
        {error && <ErrorLine>{error}</ErrorLine>}
      </Section>
    );
  }

  // Voting phase — dates or times.
  const dates = poll.phase === 'dates';
  const options = dates ? poll.dateOptions : poll.timeOptions;
  const votes = dates ? poll.dateVotes : poll.timeVotes;
  const tallies = tallyOptions(options, votes);
  const totalVoters = voterCount(votes);
  const waiting = nonVoters(roster, votes);
  // Count against the roster, not the raw ballot map: a member swapped out
  // after voting leaves a ballot behind, and "3 of 3 voted" next to "still
  // waiting on the new guy" is the contradiction that follows. Counting the
  // roster minus the holdouts always adds up to what the nag list says.
  const votedOnRoster = roster.length - waiting.length;

  return (
    <Section>
      <Heading>Draft Schedule</Heading>
      <div>
        <p className="font-display text-lg font-bold">
          {dates ? 'Which dates work?' : `What time on ${poll.chosenDate}?`}
        </p>
        <p className="mt-0.5 font-mono text-[0.7rem] text-chalk-dim">
          {dates
            ? 'Check every date you can make.'
            : `Date's locked. Check every time slot that works.`}
          {roster.length > 0
            ? ` · ${votedOnRoster} of ${roster.length} voted`
            : totalVoters > 0 && ` · ${totalVoters} voted`}
        </p>
        {options.length > 0 && roster.length > 0 && <WaitingOn waiting={waiting} myEmail={me?.email} />}
      </div>

      {options.length === 0 ? (
        <p className="font-editorial text-sm italic text-chalk-dim">
          {isAdmin
            ? 'Add the first option below.'
            : 'The commissioner is still setting the options.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {tallies.map((t) => (
            <OptionRow
              key={t.option.id}
              tally={t}
              myEmail={me?.email}
              isAdmin={isAdmin}
              readOnly={readOnly}
              lockLabel={dates ? 'Lock this date' : 'Lock this time'}
              onVote={() => run(() => voteDraftPoll(season, t.option.id))}
              onRemove={() => run(() => removeDraftOption(season, t.option.id))}
              onLock={() => run(() => lockDraftPoll(season, t.option.id))}
            />
          ))}
        </ul>
      )}

      {isAdmin && (
        <AdminBar
          placeholder={dates ? 'Add a date, e.g. Sat Aug 30' : 'Add a time, e.g. 7:00 PM'}
          onAdd={(label) => run(() => addDraftOption(season, label))}
          onReopen={dates ? undefined : () => run(() => reopenDraftPoll(season))}
          onReset={() => run(() => resetDraftPoll(season))}
        />
      )}

      {error && <ErrorLine>{error}</ErrorLine>}
    </Section>
  );
}

function OptionRow({
  tally,
  myEmail,
  isAdmin,
  readOnly,
  lockLabel,
  onVote,
  onRemove,
  onLock,
}: {
  tally: OptionTally;
  myEmail?: string;
  isAdmin: boolean;
  readOnly: boolean;
  lockLabel: string;
  onVote: () => void;
  onRemove: () => void;
  onLock: () => void;
}) {
  const { option, voters, count } = tally;
  const mine = myEmail ? voters.some((v) => v.email === myEmail) : false;

  return (
    <li className="card flex flex-col gap-2 p-3 sm:flex-row sm:items-center">
      <button
        onClick={onVote}
        disabled={readOnly}
        className={`flex flex-1 items-center gap-3 text-left ${readOnly ? 'cursor-default' : ''}`}
        aria-pressed={mine}
      >
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs ${
            mine ? 'border-brass bg-brass text-felt' : 'border-felt-line text-transparent'
          }`}
        >
          ✓
        </span>
        <span className="font-display font-bold">{option.label}</span>
        <span className="font-mono text-xs text-chalk-dim tabular">{count}</span>
      </button>

      <div className="flex items-center gap-2 pl-8 sm:pl-0">
        <Voters voters={voters} myEmail={myEmail} />
        {isAdmin && (
          <div className="flex items-center gap-1">
            <button onClick={onLock} className={btnMini} title={lockLabel}>
              Lock
            </button>
            <button
              onClick={onRemove}
              className="rounded px-1.5 py-0.5 font-mono text-xs text-chalk-dim hover:text-blood"
              aria-label={`Remove ${option.label}`}
            >
              ✕
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Who checked this option, by name. A pile of avatars looked tidy but you
 * couldn't tell who was in it — the whole point is knowing who to go bug.
 */
function Voters({
  voters,
  myEmail,
}: {
  voters: { email: string; name: string }[];
  myEmail?: string;
}) {
  if (voters.length === 0) {
    return <span className="font-mono text-[0.7rem] italic text-chalk-dim">nobody yet</span>;
  }
  return (
    <p
      className="font-mono text-[0.7rem] leading-tight text-chalk-dim sm:max-w-[18rem] sm:text-right"
      title={voters.map((v) => v.name).join(', ')}
    >
      {sortMeFirst(voters, myEmail)
        .map((v) => (v.email.toLowerCase() === myEmail?.toLowerCase() ? 'You' : firstName(v.name)))
        .join(', ')}
    </p>
  );
}

/** The nag list: everyone on the roster who hasn't touched the poll at all. */
function WaitingOn({ waiting, myEmail }: { waiting: PollMember[]; myEmail?: string }) {
  if (waiting.length === 0) {
    return (
      <p className="mt-1 font-mono text-[0.7rem] text-cash">Everyone's voted. Lock it in.</p>
    );
  }
  return (
    <p className="mt-1 font-mono text-[0.7rem] text-brass">
      Still waiting on:{' '}
      <span className="text-chalk">
        {sortMeFirst(waiting, myEmail)
          .map((m) => (m.email.toLowerCase() === myEmail?.toLowerCase() ? 'you' : m.name))
          .join(', ')}
      </span>
    </p>
  );
}

/** You first — you're the one name in the list you don't have to read. */
function sortMeFirst<T extends { email: string }>(people: T[], myEmail?: string): T[] {
  if (!myEmail) return people;
  const me = myEmail.toLowerCase();
  return [...people].sort(
    (a, b) => Number(b.email.toLowerCase() === me) - Number(a.email.toLowerCase() === me),
  );
}

/** "Dana Whitfield" -> "Dana". Keeps a ten-name row on one or two lines. */
function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

function AdminBar({
  placeholder,
  onAdd,
  onReopen,
  onReset,
}: {
  placeholder: string;
  onAdd: (label: string) => void;
  onReopen?: () => void;
  onReset: () => void;
}) {
  const [label, setLabel] = useState('');
  const add = () => {
    const l = label.trim();
    if (!l) return;
    onAdd(l);
    setLabel('');
  };
  return (
    <div className="space-y-2 border-t border-felt-line pt-3">
      <div className="flex gap-2">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          maxLength={80}
          placeholder={placeholder}
          className="flex-1 rounded-md border border-felt-line bg-felt-raised px-3 py-1.5 font-mono text-base text-chalk placeholder:text-chalk-dim focus:border-brass focus:outline-none mouse:text-sm"
        />
        <button onClick={add} disabled={!label.trim()} className={btnPrimary}>
          Add
        </button>
      </div>
      <div className="flex gap-3 font-mono text-[0.7rem] uppercase tracking-wider text-chalk-dim">
        {onReopen && (
          <button onClick={onReopen} className="hover:text-chalk">
            ← back to dates
          </button>
        )}
        <button onClick={onReset} className="hover:text-blood">
          start over
        </button>
      </div>
    </div>
  );
}

function FinalCard({ date, time }: { date: string; time: string }) {
  return (
    <div className="card border-brass/40 p-5 text-center">
      <p className="eyebrow text-brass">The draft is set</p>
      <p className="mt-2 font-display text-2xl font-black uppercase tracking-tight">{date}</p>
      <p className="mt-1 font-mono text-lg text-cash">{time}</p>
    </div>
  );
}

// --- little shared bits ---

const btnPrimary =
  'shrink-0 rounded-md bg-brass px-3 py-1.5 font-display text-sm font-bold text-felt hover:opacity-90 disabled:opacity-40';
const btnGhost =
  'rounded-md border border-felt-line px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-chalk-dim hover:text-chalk';
const btnGhostDanger =
  'rounded-md border border-felt-line px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-chalk-dim hover:text-blood';
const btnMini =
  'rounded border border-brass/50 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-brass hover:bg-brass/15';

function Section({ children }: { children: React.ReactNode }) {
  return <section className="space-y-3">{children}</section>;
}
function Heading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display text-lg font-black uppercase tracking-tight">{children}</h2>
  );
}
function ErrorLine({ children }: { children: React.ReactNode }) {
  return <p className="font-mono text-xs text-blood">{children}</p>;
}
